import { httpClient } from '@/core/api/httpClient';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import type { NotificationDtoPagedResult, NotificationsQueryParams } from '../types';
import { notificationsMock, notificationsUnreadMock } from './notifications.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

/**
 * User-scoped, not tenant-scoped: the notification feed spans every organization
 * the user belongs to, so requests skip the `X-Tenant-Id` header entirely — same
 * pattern as `/api/me/tenants`/`/api/me/invitations`.
 */
export const notificationsService = {
  getNotifications: async (
    params: NotificationsQueryParams,
    signal?: AbortSignal,
  ): Promise<NotificationDtoPagedResult> => {
    if (USE_MOCK) {
      return params.isRead === false ? notificationsUnreadMock : notificationsMock;
    }
    const { data } = await httpClient.get<NotificationDtoPagedResult>(
      `${API_V1}/me/notifications`,
      {
        params: toPascalCaseParams(params as Record<string, unknown>),
        signal,
        skipTenantHeader: true,
      },
    );
    return data;
  },

  markAsRead: async (id: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.patch<Record<string, never>, void>(
      `${API_V1}/me/notifications/${id}/read`,
      {},
      { skipTenantHeader: true },
    );
  },

  markAllAsRead: async (): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.patch<Record<string, never>, void>(
      `${API_V1}/me/notifications/read-all`,
      {},
      { skipTenantHeader: true },
    );
  },
};
